<script setup>
import {computed, reactive, watchEffect} from "vue";
import {useStore} from "vuex";
import {useRoute} from "vue-router";
import {useEOD} from "@utilities/reports/composables/useEOD.js";
import {useUserData} from "@composables/useUserData.js";
import {formatDateLong, formatUSD} from "@utilities/helpers.ts";
import ServiceSalesTable from "@components/reports/EOD/ServiceSalesTable.vue";
import ServiceTypeTable from "@components/reports/EOD/ServiceTypeTable.vue";
import SmogTable from "@components/reports/EOD/SmogTable.vue";
import RevenueTable from "@components/reports/EOD/RevenueTable.vue";
import ReportTableBox from "@components/reports/EOD/ReportTableBox.vue";
import NewRowForm from "@components/reports/EOD/NewRowForm.vue";

// <editor-fold desc="Boilerplate">-------------------------------------------------

const route = useRoute();
const store = useStore();
const date = computed(() => {
  if (route.params['date']) {
    return new Date(route.params['date'] + 'T00:00:00') // including the time makes the Date instantiation use the local timezone.
  }
  return new Date(); // otherwise return today
})
const shopId = computed(() => {
  if (route.params['shopId']) {
    return Number(route.params['shopId'])
  }
  if (store.state.currentShopID) {
    return Number(store.state.currentShopID)
  }
  return null
})
const {
  getEODReport, saveEOD, addRow, editing, serviceSales, servicesRendered, smogSales,
  revenueTable, totalCC, totalCash, totalRev, totalSales, smogRejects, difference, totalServiceSales, totalServices,
  totalSmogs, totalSmogSales, checkIfSubmitted, techsOnDuty, eodSubmitted, exportEODToCSV
} = useEOD(shopId, date); // passing raw refs to guarantee reactivity
const loadingStates = reactive({
  eodSubmitted: true
})
const isLoading = computed(() => Object.values(loadingStates).some(state => state === true));

// </editor-fold>---------------------------------------------------------------------

// <editor-fold desc="Shop info">-------------------------------------

const shops = computed(() => store.state.shops);
const shopName = computed(() => {
  const shop = shops.value.find(s => s.id === shopId.value);
  return shop ? shop.name : '';
});

// </editor-fold>------------------------------------------------------

// <editor-fold desc="Report Data">-----------------------------------

watchEffect(async () => {
  loadingStates.eodSubmitted = true
  try {
    await getEODReport(date.value, shopId.value);
  } catch (error) {
    console.error('Error fetching EOD report:', error);
  } finally {
    loadingStates.eodSubmitted = false
  }
}, {immediate: true});
watchEffect(async () => {
  loadingStates.eodSubmitted = true
  try {
    eodSubmitted.value = await checkIfSubmitted(date.value, shopId.value)
  } catch (error) {
    console.error('Error checking submission status:', error)
    eodSubmitted.value = false
  } finally {
    loadingStates.eodSubmitted = false
  }
})
const reportHeader = computed(() => {
  const headerDate = date.value !== null ? formatDateLong(date.value) : 'No Date Selected';
  const turnedIn = eodSubmitted.value ? ' - Turned In' : ' - Not Turned In';
  return `${shopName.value} Shop, ${headerDate}${turnedIn}`;
})

// </editor-fold>------------------------------------------------------

// <editor-fold desc="Editing">----------------------------------------

watchEffect(() => {
  editing.value = !eodSubmitted.value;
}, {immediate: true});
const toggleEditing = () => {
  editing.value = !editing.value;
}

// </editor-fold>------------------------------------------------------

// <editor-fold desc="User Data">--------------------------------------

const {allUsers} = useUserData();
watchEffect(() => {
}, {immediate: true});
const getUserDisplayName = (id) => {
  const users = allUsers.value;
  return users.find(user => user.ID === id).data['display_name'];
}

// </editor-fold>------------------------------------------------------

</script>

<template>
  <div class="mb-4 max-w-[1280px] w-full">
    <Panel :header="reportHeader">
      <template #icons>
        <div class="flex gap-2 w-fit">
          <template v-if="eodSubmitted">
            <template v-if="editing">
              <Button size="small" label="Cancel" @click="toggleEditing" severity="secondary"/>
              <NewRowForm @add-row="addRow"/>
              <Button size="small" label="Save" @click="saveEOD"/>
            </template>
            <Button v-else size="small" label="Edit" @click="toggleEditing" outlined severity="secondary"/>
            <Button v-if="eodSubmitted && !editing" size="small" label="Export to CSV" @click="exportEODToCSV"
                    severity="primary"/>
          </template>
          <Button v-else size="small" label="Submit" @click="saveEOD"/>
        </div>
      </template>
      <!-- Main flex row container -->
      <div class="flex flex-col gap-4">
        <ReportTableBox title="Summary">
          <div class="flex w-full px-4">
            <!-- Column 1: Techs -->
            <div class="flex flex-col gap-2 w-1/3 py-4 items-center">
              <!-- Techs -->
              <div class="flex flex-col justify-center items-center w-full gap-2">
                <span class="font-semibold text-lg">Technicians on Duty:</span>
                <MultiSelect
                    v-if="editing"
                    v-model="techsOnDuty"
                    display="chip"
                    :options="allUsers"
                    optionLabel="data.display_name"
                    optionValue="ID"
                    class="w-full"
                    placeholder="Select Technicians"
                />
                <div v-else class="flex flex-wrap gap-1">
                  <div v-if="!techsOnDuty.length || techsOnDuty.length === 0"
                       class="flex w-full h-full justify-center items-center">
                    <span class="text-slate-400">None assigned.</span>
                  </div>
                  <Tag
                      v-for="t in techsOnDuty"
                      :key="t"
                      :value="getUserDisplayName(t)"
                      severity="secondary"
                  />
                </div>
              </div>
            </div>
            <Divider layout="vertical"/>

            <!-- Column 2: Financial Metrics -->
            <div class="flex flex-col gap-4 w-1/3 py-4">
              <div class="flex justify-around w-full">
                <!-- Cash / CC Column-->
                <div class="flex flex-col w-2/5 gap-2">
                  <!-- Cash -->
                  <div class="flex items-center w-full justify-between">
                    <div class="font-bold">Cash:</div>
                    <div>${{ totalCash }}</div>
                  </div>
                  <!-- CC -->
                  <div class="flex items-center w-full justify-between">
                    <div class="font-bold">CC:</div>
                    <div>${{ totalCC }}</div>
                  </div>
                </div>
                <!-- Revenue Column -->
                <div class="flex flex-col w-2/5 gap-2">
                  <!-- Revenue -->
                  <div class="flex w-full items-center justify-between">
                    <div class="justify-between font-bold">Revenue:</div>
                    <div class="justify-between">{{ formatUSD(totalRev) }}</div>
                  </div>
                  <!-- Total Sales -->
                  <div class="flex w-full items-center justify-between">
                    <div class="font-bold">Sales:</div>
                    <div>{{ formatUSD(totalSales) }}</div>
                  </div>
                  <!-- Difference -->
                  <div class="flex w-full items-center justify-between">
                    <div class="font-bold">+/-:</div>
                    <div>{{ formatUSD(difference) }}</div>
                  </div>
                </div>
              </div>
            </div>
            <Divider layout="vertical"/>

            <!-- Column 3: Service Metrics -->
            <div class="flex flex-col gap-2 w-1/3 py-4">
              <div class="flex justify-around w-full">
                <div class="flex flex-col w-2/5 gap-2">
                  <!-- Smog -->
                  <div class="flex items-center w-full justify-between">
                    <div class="font-bold">Smog:</div>
                    <div>{{ totalSmogs }}</div>
                  </div>
                  <!-- Services -->
                  <div class="flex items-center w-full justify-between">
                    <div class="font-bold">Services:</div>
                    <div>{{ totalServices }}</div>
                  </div>
                </div>
                <!-- Rejects -->
                <div class="flex flex-col w-2/5 gap-2">
                  <div :class="['flex', 'items-center', 'w-full', 'gap-2', 'justify-between', {'-mt-1.5': editing}]">
                    <div class="font-bold">Rejects:</div>
                    <div>
                      <InputNumber v-if="editing" inputClass="w-full" v-model="smogRejects" size="small"/>
                      <template v-else>{{ smogRejects }}</template>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ReportTableBox>
        <div class="flex gap-4 justify-center">
          <div class="w-1/4">
            <ServiceSalesTable :editing="editing" v-model="serviceSales"
                               :total="totalServices" :totalSales="totalServiceSales"/>
          </div>
          <div class="w-1/6">
            <ServiceTypeTable :editing="editing" :data-table="servicesRendered"/>
          </div>
          <div class="flex flex-col gap-4 w-1/4">
            <SmogTable
                :editing="editing" :data-table="smogSales"
                :total="totalSmogs" :total-sales="totalSmogSales"/>
            <RevenueTable
                :editing="editing" :data-table="revenueTable"
                :total="totalRev"/>
          </div>
        </div>
        <div class="mt-0 flex justify-end">
          <div class="flex gap-2 w-fit">
          <template v-if="eodSubmitted">
            <template v-if="editing">
              <Button size="small" label="Cancel" @click="toggleEditing" severity="secondary"/>
              <NewRowForm @add-row="addRow"/>
              <Button size="small" label="Save" @click="saveEOD"/>
            </template>
            <Button v-else size="small" label="Edit" @click="toggleEditing" outlined severity="secondary"/>
            <Button v-if="eodSubmitted && !editing" size="small" label="Export to CSV" @click="exportEODToCSV"
                    severity="primary"/>
          </template>
          <Button v-else size="small" label="Submit" @click="saveEOD"/>
          </div>
        </div>
      </div>
    </Panel>
  </div>
</template>
